import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Row} from "reactstrap";
import Button from "../../../../../../components/base/button/button";
import {Add} from "@mui/icons-material";
import SearchInput from "../../../../../../components/base/input/search_input";
import Api from "../../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import AddAndEditOperatorGroupModal from "./modal/add-edit";
import {OperatorsGroupsListDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {Link} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const dataGridSaveConfig = {
    name: 'CurrentOperatorGroupsListViewDataGrid',
    version: 1,
}

const CurrentOperatorGroupsListView = () => {
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [keywords, setKeywords] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [group, setGroup] = useState();
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const {configureNotebookTags} = useConfigureNotebook('operator-groups-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.operatorsGroups);
        configureBehavioralTool((tags) => tags.operatorsGroups);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: 'Title',
                name: OperatorsGroupsListDataGridCellKeys.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                }
            },
            ...(operatorAccessLevels.packages.dashboard.systemWideDashboards ? [{
                name: OperatorsGroupsListDataGridCellKeys.editDashboards,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 150,
            }] : []),
            {
                name: OperatorsGroupsListDataGridCellKeys.edit,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 130,
            },
            ...(operatorAccessLevels.packages.base.manageAccessLevels ? [
                {
                    name: OperatorsGroupsListDataGridCellKeys.editAccessLevels,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    resizable: false,
                    width: 150,
                },
            ] : []),
        ], [operatorAccessLevels.packages.base.manageAccessLevels])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version of operator groups to be shown on the table based on the changes in the keyword and groups in the inner state
     * @type {unknown}
     */
    const filteredGroups = useMemo(() => {
        if (keywords && keywords.length) {
            return groups.filter(e => e.name.toLowerCase().includes(keywords.toLowerCase().trim()));
        } else {
            return groups;
        }
    }, [keywords, groups])

    /**
     * Gets all the operator groups available to be assigned to different users.
     * @type {function(): Promise<void>}
     */
    const getAllGroups = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllOperatorGroups();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setGroups(response?.data ?? []);
        }
        setLoading(false);
    }, [isMounted])

    /**
     * Gets all the available user groups as soon as the component mounts
     */
    useEffect(() => {
        void getAllGroups();
    }, [getAllGroups])

    /**
     * Event Listener for the keyword changes in the search input. Will save the keywords, and search the groups
     * with the keyword
     * @type {function(*): void}
     */
    const searchByKeyword = useCallback((keywords) => {
        setKeywords(keywords);
    }, [])

    /**
     * Opens the modal for adding a new record
     * @type {function(): void}
     */
    const onAddGroup = useCallback(() => {
        setOpenModal(true);
        setGroup(null);
    }, [])

    /**
     * Adds the newly created operator to the beginning of the list and slices the list to show the maximum of
     * [initialPaginationInfo.pageSize] number of items
     * @type {function(): void}
     */
    const onAddSuccess = useCallback(() => {
        void getAllGroups();
    }, [getAllGroups])

    /**
     * Adds the newly created operator to the beginning of the list and slices the list to show the maximum of
     * [initialPaginationInfo.pageSize] number of items
     *
     * @param group {any} the record that has been edited
     */
    const onEditSuccess = useCallback((group) => {
        setGroups(prevState => prevState.map(e => e.id === group.id ? group : e));
    }, [])

    /**
     * Opens the modal for editing a record
     */
    const onEditGroup = useCallback((group) => {
        setOpenModal(true);
        setGroup(group);
    }, [])

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return filteredGroups?.map(group => ({
            key: group.id,
            data: group,
            cells: {
                [OperatorsGroupsListDataGridCellKeys.title]: group.name,
                [OperatorsGroupsListDataGridCellKeys.editDashboards]:
                    <Button
                        disabled={loading}
                        className='mx-3'
                        color={'primary'}
                        variant={'outlined'}
                        component={Link}
                        to={routeFunctions.operators.dashboards.single(group.id)}
                    >
                        Edit Dashboards
                    </Button>,
                [OperatorsGroupsListDataGridCellKeys.edit]:
                    <Button
                        disabled={loading}
                        className='mx-3'
                        color={'primary'}
                        variant={'outlined'}
                        onClick={() => onEditGroup(group)}
                    >
                        Edit
                    </Button>,
                [OperatorsGroupsListDataGridCellKeys.editAccessLevels]:
                    <Button
                        disabled={loading}
                        className='mx-3'
                        color={'primary'}
                        variant={'outlined'}
                        component={Link}
                        to={routeFunctions.operators.groups.single(group.id)}
                    >
                        Edit Access Levels
                    </Button>

            }
        })) ?? []
    }, [filteredGroups, loading, onEditGroup])


    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center my-4'>
                <h2 className={'text-xl font-weight-semi-bold m-0 ml-3'}>
                    Operator Groups
                </h2>
                <Button
                    className='mr-2 d-flex justify-content-center align-items-center'
                    variant={'outlined'}
                    color={'primary'}
                    onClick={onAddGroup}
                >
                    <Add className={`icon small`} style={{margin: '2px 0'}}/>
                    <p className='ml-2'>Add new</p>
                </Button>
            </Row>
            <SearchInput
                loading={loading}
                autoSearch={true}
                search={searchByKeyword}
                label={'Search'}
            />
            <div className='w-100 my-5'/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                hideFooterActions
                hideExporting
                hidePagination
                hidePageSize
                visibleRows={rows.length}
                state={{
                    loading: {state: loading},
                    density: dataGridDensity,
                }}
            />
            <AddAndEditOperatorGroupModal
                open={openModal}
                setOpen={setOpenModal}
                group={group}
                onSuccess={(g) => {
                    if (group) {
                        onEditSuccess(g)
                    } else {
                        onAddSuccess(g);
                    }
                }}
            />
        </>
    );
}

export default CurrentOperatorGroupsListView;
